import React, { useState } from "react";
import { AlertTriangle, FileDown, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { t } from "@/lib/negotiationLabels";

export function FinalReviewModal({ open, onOpenChange, readiness, playerName, onGenerate, generating }) {
  const [audience, setAudience] = useState("external");
  const [template, setTemplate] = useState("standard");
  const [force, setForce] = useState(false);
  const incomplete = (readiness?.sections || []).filter((s) => s.status !== "complete");
  const blocked = (readiness?.overall ?? 0) < 60 && !force;
  const dateStr = new Date().toISOString().slice(0, 10);
  const safe = (playerName || "joueur").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="final-review-modal">
        <DialogHeader>
          <DialogTitle>{t("negotiationProfile.review.title")}</DialogTitle>
          <DialogDescription>
            {t("negotiationProfile.readiness")} <b className="text-foreground">{readiness?.overall ?? 0}%</b>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {incomplete.length > 0 && (
            <div className="rounded-xl border border-warning/30 bg-warning/10 p-3" data-testid="review-missing">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-warning mb-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> {t("negotiationProfile.review.missing")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {incomplete.map((s) => (
                  <Badge key={s.id} variant="outline" className="text-[10px] bg-secondary text-muted-foreground border-border">
                    {t(`negotiationProfile.steps.${s.id}`)} · {s.score}%
                  </Badge>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-xs font-semibold mb-2">{t("negotiationProfile.review.audience")}</p>
            <RadioGroup value={audience} onValueChange={setAudience} className="space-y-2">
              <label className="flex items-center gap-3 rounded-xl border border-border px-3.5 py-2.5 cursor-pointer">
                <RadioGroupItem value="external" data-testid="review-audience-external" />
                <span className="text-sm">{t("negotiationProfile.review.external")}</span>
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-border px-3.5 py-2.5 cursor-pointer">
                <RadioGroupItem value="internal" data-testid="review-audience-internal" />
                <span className="text-sm">{t("negotiationProfile.review.internal")}</span>
              </label>
            </RadioGroup>
          </div>
          <div>
            <p className="text-xs font-semibold mb-2">Format</p>
            <RadioGroup value={template} onValueChange={setTemplate} className="space-y-2">
              <label className="flex items-center gap-3 rounded-xl border border-border px-3.5 py-2.5 cursor-pointer">
                <RadioGroupItem value="standard" data-testid="review-template-standard" />
                <span className="text-sm">{t("negotiationProfile.review.template.standard")}</span>
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-border px-3.5 py-2.5 cursor-pointer">
                <RadioGroupItem value="compact" data-testid="review-template-compact" />
                <span className="text-sm">{t("negotiationProfile.review.template.compact")}</span>
              </label>
            </RadioGroup>
          </div>
          <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
            <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
            {t("negotiationProfile.review.privacy")}
          </p>
          <p className="text-[11px] text-muted-foreground font-mono truncate">
            ATHLYNX_{safe}_Negotiation_Profile_{dateStr}.pdf
          </p>
          {(readiness?.overall ?? 0) < 60 && (
            <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="review-force-label">
              <Checkbox checked={force} onCheckedChange={(v) => setForce(!!v)} data-testid="review-force-checkbox" />
              {t("negotiationProfile.review.forceIncomplete")}
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("negotiationProfile.review.cancel")}</Button>
          <Button onClick={() => onGenerate({ audience, template_id: template })} disabled={generating || blocked}
            className="bg-athlynx hover:bg-athlynx/90 text-white gap-2" data-testid="review-generate-btn">
            <FileDown className="h-4 w-4" />
            {generating ? "Génération…" : t("negotiationProfile.review.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
